import React, { useState, useEffect, useCallback } from 'react';

import { ReloadOutlined, ToolOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Skeleton, Table, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../utils/dateTime';

interface RecordingSessionRow {
  session_id: string;
  fulfillment_id: string;
  parcel_id: string | null;
  video_index: number | null;
  status: string;
  total_chunks: number | null;
  gcs_chunk_count: number;
  fulfillment_video_id: string | null;
  failure_reason: string | null;
  last_chunk_received_at: string | null;
  created_at: string;
  updated_at: string;
}

interface OrderServiceLike {
  getRecordingSessions: (orderId: string) => Promise<any>;
  forceFinalizeRecordingSession: (sessionId: string) => Promise<any>;
}

interface Props {
  orderId: string;
  orderService: OrderServiceLike;
  // Wired through from OrderShow → AdminLayout / OperatorLayout's
  // useAdminNotification / useOperatorNotification. When present, the rescue
  // button registers the worker job_id so progress + completion are surfaced
  // via the BackgroundJobManager notification panel.
  addJob?: (jobId: string, jobName: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  finalizing: 'processing',
  completed: 'success',
  failed: 'error',
  abandoned: 'warning',
};

export default function RecordingDiagnostics({ orderId, orderService, addJob }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<RecordingSessionRow[]>([]);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orderId || !orderService?.getRecordingSessions) return;
    setLoading(true);
    setError(null);
    try {
      const data = await orderService.getRecordingSessions(orderId);
      if (data?.success) {
        setSessions(data.data?.sessions || []);
      } else {
        setError(data?.message || t('common.error.load_failed'));
      }
    } catch (e) {
      console.error('Failed to load recording sessions:', e);
      setError(t('common.error.network_error'));
    } finally {
      setLoading(false);
    }
  }, [orderId, orderService, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleForceFinalize = (row: RecordingSessionRow) => {
    Modal.confirm({
      title: t('shared.recording_diagnostics.force_finalize_confirm_title', {
        defaultValue: '強制合併此 session？',
      }),
      content: t('shared.recording_diagnostics.force_finalize_confirm_content', {
        defaultValue:
          'Worker 會掃 GCS 抓現有 chunk 合併，缺號 chunk 會跳過（可能產生跳幀）。成功後 chunk 會自動刪除。',
      }),
      okText: t('shared.recording_diagnostics.force_finalize_action', { defaultValue: '強制合併' }),
      cancelText: t('common.action.cancel', { defaultValue: '取消' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusySessionId(row.session_id);
        try {
          const data = await orderService.forceFinalizeRecordingSession(row.session_id);
          if (data?.success && data.data?.job_id) {
            // Register with the project's background-job notification flow —
            // progress / failure / completion arrive via NotificationChannel,
            // not by polling this table.
            if (addJob) {
              addJob(
                data.data.job_id,
                t('shared.recording_diagnostics.force_finalize_job_name', {
                  defaultValue: '強制合併錄影 session',
                }),
              );
            }
            message.success(
              t('shared.recording_diagnostics.force_finalize_enqueued', {
                defaultValue: '已排入合併佇列，可在右上方任務通知查看進度',
              }),
            );
            await load();
          } else {
            message.error(
              data?.message ||
                t('shared.recording_diagnostics.force_finalize_failed', {
                  defaultValue: '無法排入合併佇列',
                }),
            );
          }
        } catch (e) {
          console.error('force_finalize failed:', e);
          message.error(t('common.error.operation_failed'));
        } finally {
          setBusySessionId(null);
        }
      },
    });
  };

  if (loading) return <Skeleton active paragraph={{ rows: 2 }} />;

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  if (sessions.length === 0) {
    return (
      <span style={{ color: '#999' }}>
        {t('shared.recording_diagnostics.empty', { defaultValue: '此訂單無錄影 session 記錄' })}
      </span>
    );
  }

  const columns = [
    {
      title: t('shared.recording_diagnostics.session_id', { defaultValue: 'Session ID' }),
      dataIndex: 'session_id',
      key: 'session_id',
      render: (v: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v.slice(0, 8)}…</span>
      ),
    },
    {
      title: t('shared.recording_diagnostics.status', { defaultValue: '狀態' }),
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => (
        <Tag color={STATUS_COLORS[s] || 'default'}>
          {t(`shared.recording_diagnostics.status_value.${s}`, { defaultValue: s })}
        </Tag>
      ),
    },
    {
      title: t('shared.recording_diagnostics.chunks', { defaultValue: 'Chunks' }),
      key: 'chunks',
      render: (_: any, row: RecordingSessionRow) => {
        const expected = row.total_chunks;
        const present = row.gcs_chunk_count;
        if (expected == null) return <span>{present}</span>;
        const ok = present >= expected;
        return (
          <span style={{ color: ok ? undefined : '#cf1322' }}>
            {present} / {expected}
          </span>
        );
      },
    },
    {
      title: t('shared.recording_diagnostics.parcel', { defaultValue: 'Parcel' }),
      dataIndex: 'parcel_id',
      key: 'parcel_id',
      render: (v: string | null) =>
        v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v.slice(0, 8)}…</span> : '-',
    },
    {
      title: t('shared.recording_diagnostics.created_at', { defaultValue: '建立時間' }),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('shared.recording_diagnostics.failure_reason', { defaultValue: '失敗原因' }),
      dataIndex: 'failure_reason',
      key: 'failure_reason',
      render: (v: string | null) =>
        v ? <span style={{ color: '#cf1322', fontSize: 12 }}>{v}</span> : '-',
    },
    {
      title: t('common.action.action', { defaultValue: '操作' }),
      key: 'action',
      render: (_: any, row: RecordingSessionRow) => {
        const alreadyMerged = !!row.fulfillment_video_id;
        const noChunks = row.gcs_chunk_count === 0;
        const disabled = alreadyMerged || noChunks || row.status === 'finalizing';
        let title: string | undefined;
        if (alreadyMerged) {
          title = t('shared.recording_diagnostics.disabled_already_merged', {
            defaultValue: '已合併完成',
          });
        } else if (noChunks) {
          title = t('shared.recording_diagnostics.disabled_no_chunks', {
            defaultValue: 'GCS 中已無 chunk',
          });
        } else if (row.status === 'finalizing') {
          title = t('shared.recording_diagnostics.disabled_in_progress', {
            defaultValue: '正在合併中',
          });
        }
        return (
          <Button
            size="small"
            danger
            icon={<ToolOutlined />}
            disabled={disabled}
            loading={busySessionId === row.session_id}
            onClick={() => handleForceFinalize(row)}
            title={title}
          >
            {t('shared.recording_diagnostics.force_finalize_action', { defaultValue: '強制合併' })}
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>
          {t('common.action.refresh', { defaultValue: '重新整理' })}
        </Button>
      </div>
      <Table
        rowKey="session_id"
        size="small"
        pagination={false}
        columns={columns as any}
        dataSource={sessions}
      />
    </div>
  );
}
